/**
 * Color and white bulb action preparations
 */

import { COLOR_BULBS } from 'src/constants';
import { convertUiPropertyToSceneCommand } from 'src/components/Scenes/helpers/utilities_ui_to_scenes';
import { value_from_percent_range } from 'src/helpers/utilities';
import { isCctOnlyDevice, isRgbwwPm, isXMODDevice } from 'src/helpers/interface_helpers';
import type {
	DeviceActionContext,
	PreparedDeviceAction,
	ColorBulbActionName,
	ColorBulbActionHandler,
	CCTBulbActionName,
	CCTBulbActionHandler,
	DeviceActionGetter,
} from 'src/components/Scenes/helpers/device_actions/types';

/**
 * Applies on/off state and mode transitions for color-capable bulbs.
 */
const handleColorTurn: ColorBulbActionHandler = ({ action, set }) => {
	set.state = action.value;
};

/**
 * Applies mode changes for color-capable bulbs.
 */
const handleColorMode: ColorBulbActionHandler = ({ action, set }) => {
	set.mode = action.value;
};

/**
 * Applies transition duration for color-capable bulbs.
 */
const handleColorTransitionDuration: ColorBulbActionHandler = ({ action, set }) => {
	set.transition_duration = action.value;
};

/**
 * Applies temperature adjustments for color-capable bulbs.
 */
const handleColorTemperature: ColorBulbActionHandler = ({ action, set }) => {
	set.temp = action.value;
};

/**
 * Applies RGB payload for color-capable bulbs. Executed only when the red channel is processed.
 */
const handleColorRgb: ColorBulbActionHandler = ({ action, set, getAction }) => {
	if (action.name !== 'r') {
		return;
	}

	const green = getAction('g');
	const blue = getAction('b');

	if (!green || !blue) {
		return;
	}

	set.r = action.value;
	set.g = green.value;
	set.b = blue.value;
};

/**
 * Applies brightness/gain adjustments for color-capable bulbs.
 */
const handleColorBrightness: ColorBulbActionHandler = ({ action, set, getAction }) => {
	const brightnessValue = action.value;
	const modeAction = getAction('mode');
	const tempAction = getAction('temp', 'ct');

	if (tempAction || modeAction?.value === 'white') {
		set.brightness = brightnessValue;
		return;
	}

	if (modeAction?.value === 'color') {
		set.gain = brightnessValue;
		return;
	}

	set.gain = brightnessValue;
	set.brightness = brightnessValue;
};

/**
 * Applies white channel adjustments for color-capable bulbs.
 */
const handleColorWhiteChannel: ColorBulbActionHandler = ({ action, set, context }) => {
	set[convertUiPropertyToSceneCommand('white_channel', context.channel)] = value_from_percent_range(
		action.value,
		[0, 255],
	);
};

/**
 * Mapping of color bulb action names to their respective handlers.
 */
const COLOR_BULB_ACTION_HANDLERS: Partial<Record<ColorBulbActionName, ColorBulbActionHandler>> = {
	turn: handleColorTurn,
	state: handleColorTurn,
	mode: handleColorMode,
	transition_duration: handleColorTransitionDuration,
	temp: handleColorTemperature,
	ct: handleColorTemperature,
	r: handleColorRgb,
	g: handleColorRgb,
	b: handleColorRgb,
	brightness: handleColorBrightness,
	gain: handleColorBrightness,
	white_channel: handleColorWhiteChannel,
};

/**
 * Exported list of supported action names for color bulb devices.
 */
export const COLOR_BULB_SUPPORTED_ACTIONS = Object.keys(
	COLOR_BULB_ACTION_HANDLERS,
) as ColorBulbActionName[];

/**
 * Applies turn/state actions for CCT bulbs.
 */
const handleCctTurn: CCTBulbActionHandler = ({ action, set, context }) => {
	set[convertUiPropertyToSceneCommand('turn', context.channel)] = action.value;
};

/**
 * Applies temperature adjustments for CCT bulbs.
 */
const handleCctTemperature: CCTBulbActionHandler = ({ action, set, context }) => {
	set[convertUiPropertyToSceneCommand('ct', context.channel)] = action.value;
};

/**
 * Applies brightness adjustments for CCT bulbs.
 */
const handleCctBrightness: CCTBulbActionHandler = ({ action, set, context }) => {
	set[convertUiPropertyToSceneCommand('brightness', context.channel, 'white')] = action.value;
};

/**
 * Applies transition duration adjustments for CCT bulbs.
 */
const handleCctTransitionDuration: CCTBulbActionHandler = ({ action, set, context }) => {
	set[convertUiPropertyToSceneCommand('transition_duration', context.channel)] = action.value;
};

/**
 * Mapping of CCT bulb action names to their respective handlers.
 */
const CCT_BULB_ACTION_HANDLERS: Partial<Record<CCTBulbActionName, CCTBulbActionHandler>> = {
	turn: handleCctTurn,
	state: handleCctTurn,
	temp: handleCctTemperature,
	ct: handleCctTemperature,
	brightness: handleCctBrightness,
	gain: handleCctBrightness,
	transition_duration: handleCctTransitionDuration,
};

/**
 * Exported list of supported action names for CCT bulb devices.
 */
export const CCT_BULB_SUPPORTED_ACTIONS = Object.keys(CCT_BULB_ACTION_HANDLERS) as CCTBulbActionName[];

/**
 * Prepares action payload for color-capable bulbs.
 *
 * Supports translating multiple UI scene commands (turn, brightness, color, etc.)
 * into the device setup payload.
 *
 * @param context - Context containing shared helpers, channel metadata, and queued device actions.
 * Supported action names are listed in `COLOR_BULB_SUPPORTED_ACTIONS`.
 * @returns Prepared action payload ready for dispatch to the device.
 */
export const prepareColorBulbAction = (context: DeviceActionContext): PreparedDeviceAction => {
	const { hexToDecimal, actions } = context;

	const action: PreparedDeviceAction = {
		_gui_purpose: 'action',
		r: 'dev:' + hexToDecimal + ':setup',
	};

	const set: Record<string, unknown> = {
		_isdata: true,
	};

	const getAction: DeviceActionGetter = (...names) =>
		actions.find((actionEl) => names.includes(actionEl.name));

	for (const actionEl of actions) {
		const handler = COLOR_BULB_ACTION_HANDLERS[actionEl.name as ColorBulbActionName];
		if (!handler) {
			continue;
		}
		handler({
			action: actionEl,
			context,
			set,
			getAction,
		});
	}

	action.set = { ...set };

	return action;
};

/**
 * Prepares action payload for CCT (Correlated Color Temperature) bulbs.
 *
 * @param context - Context containing shared helpers, channel metadata, and queued device actions.
 * Supported action names are listed in `CCT_BULB_SUPPORTED_ACTIONS`.
 * @returns Prepared action payload ready for dispatch to the device.
 */
export const prepareCCTBulbAction = (context: DeviceActionContext): PreparedDeviceAction => {
	const { hexToDecimal, actions } = context;

	const action: PreparedDeviceAction = {
		_gui_purpose: 'action',
		r: 'dev:' + hexToDecimal + ':setup',
	};

	const set: Record<string, unknown> = {
		_isdata: true,
	};

	const getAction: DeviceActionGetter = (...names) =>
		actions.find((actionEl) => names.includes(actionEl.name));

	for (const actionEl of actions) {
		const handler = CCT_BULB_ACTION_HANDLERS[actionEl.name as CCTBulbActionName];
		if (!handler) {
			continue;
		}
		handler({
			action: actionEl,
			context,
			set,
			getAction,
		});
	}

	action.set = { ...set };

	return action;
};

/**
 * Determines whether a device operates as a color bulb.
 *
 * @param type - Device type identifier.
 * @param mode - Active device mode.
 * @param channelMode - Optional specific channel mode for multi-channel devices.
 * @returns `true` if the device supports RGB color output, otherwise `false`.
 */
export const isColorBulb = (type: string, mode: string, channelMode?: string): boolean => {
	return (
		COLOR_BULBS.includes(type) ||
		(type === 'SHRGBW2' && mode === 'color') ||
		(type === 'SNDC-0D4P10WW' && mode.indexOf('rgb') !== -1) ||
		(type === 'SPDC-0D5PE16EU' && channelMode === 'rgb') ||
		type === 'S3BL-C010007AEU' ||
		type === 'S3BL-D010009AEU' ||
		type === 'SHBDUO-1'
	);
};

/**
 * Determines whether a device operates in CCT mode.
 *
 * @param type - Device type identifier.
 * @param channelMode - Optional specific channel mode for multi-channel devices.
 * @param mode - Optional device mode (used for XMOD devices like S3MX and CCT-only devices).
 * @returns `true` if the device is in CCT mode, otherwise `false`.
 */
export const isCCTMode = (type: string, channelMode?: string, mode?: string): boolean => {
	if (isXMODDevice(type)) {
		return mode === 'cct' || channelMode === 'cct';
	}
	if (isCctOnlyDevice(type)) {
		return true;
	}
	return channelMode === 'cct' && isRgbwwPm(type);
};
